import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Camera, Trash2 } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { backendUrl } from "@/components/SongCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

export default function Settings() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const fileRef = useRef(null);
  const [form, setForm] = useState({ display_name: "", bio: "", country: "", is_private: false });

  useEffect(() => { if (user) setForm({ display_name: user.display_name || "", bio: user.bio || "", country: user.country || "", is_private: !!user.is_private }); }, [user]);

  const save = async () => {
    try { await api.patch("/me", form); await refresh(); toast.success("Profile updated."); } catch (e) { toast.error(apiErr(e)); }
  };
  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post("/me/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } }); await refresh(); toast.success("Photo updated."); }
    catch (er) { toast.error(apiErr(er)); }
  };
  const del = async () => {
    if (!window.confirm("Delete your account permanently? This anonymizes your profile and removes your recordings. If you have an active subscription, cancel it first in Billing.")) return;
    try { await api.delete("/me"); await logout(); toast.success("Account deleted."); nav("/"); } catch (e) { toast.error(apiErr(e)); }
  };

  if (!user) return null;
  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="font-head text-4xl font-black tracking-tighter mb-8">Settings</h1>

      <div className="glass rounded-3xl p-8 space-y-6">
        <div className="flex items-center gap-5">
          <div className="relative">
            <div className="w-20 h-20 rounded-full overflow-hidden bg-neon-purple/30 flex items-center justify-center text-2xl font-head font-bold">
              {user.avatar_url ? <img src={backendUrl(user.avatar_url)} className="w-full h-full object-cover" alt="" /> : (user.display_name || "U")[0].toUpperCase()}
            </div>
            <button onClick={() => fileRef.current?.click()} className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-neon-pink flex items-center justify-center" data-testid="avatar-upload"><Camera className="w-4 h-4" /></button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadAvatar} />
          </div>
          <div><p className="font-head font-bold text-lg">@{user.username}</p><p className="text-sm text-muted-foreground">{user.email}</p></div>
        </div>

        <div><Label className="text-xs">Display name</Label><Input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} className="mt-1.5 bg-input border-white/10" data-testid="set-name" /></div>
        <div><Label className="text-xs">Bio</Label><Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="mt-1.5 bg-input border-white/10" placeholder="Tell the world about your sound..." data-testid="set-bio" /></div>
        <div><Label className="text-xs">Country</Label><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="mt-1.5 bg-input border-white/10" data-testid="set-country" /></div>
        <div className="flex items-center justify-between"><div><p className="text-sm font-semibold">Private profile</p><p className="text-xs text-muted-foreground">Hide your public performances</p></div><Switch checked={form.is_private} onCheckedChange={(v) => setForm({ ...form, is_private: v })} data-testid="set-private" /></div>

        <Button onClick={save} className="w-full rounded-full bg-neon-pink hover:bg-neon-pink/90 font-bold py-6" data-testid="save-settings">Save changes</Button>
      </div>

      <div className="mt-8 rounded-3xl p-6 border border-destructive/40 bg-destructive/5">
        <h3 className="font-head font-bold text-destructive mb-2">Danger zone</h3>
        <p className="text-sm text-muted-foreground mb-4">Deleting your account is permanent. Your recordings will be removed and your profile anonymized.</p>
        <Button onClick={del} variant="destructive" className="rounded-full" data-testid="delete-account"><Trash2 className="w-4 h-4 mr-2" /> Delete account</Button>
      </div>
    </div>
  );
}
